/**
 * @fileoverview Users Tab for Institution Central
 * Displays users linked to the institution
 */

import React from "react";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Chip,
    IconButton,
    Tooltip
} from "@mui/material";
import { intl } from "dmsbasereact";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicationIcon from "@mui/icons-material/Medication";
import BusinessIcon from "@mui/icons-material/Business";
import { UserRole } from "../../../constants/UserRole";

/**
 * Get role info (icon, color, label)
 */
const getRoleInfo = (role) => {
    switch (role) {
        case UserRole.DOCTOR:
            return {
                icon: <LocalHospitalIcon fontSize="small" />,
                color: "primary",
                label: intl.get("Doctor").d("Doctor")
            };
        case UserRole.PHARMACIST:
            return {
                icon: <MedicationIcon fontSize="small" />,
                color: "secondary",
                label: intl.get("Pharmacist").d("Pharmacist")
            };
        case UserRole.INSTITUTION_RESPONSIBLE:
            return {
                icon: <BusinessIcon fontSize="small" />,
                color: "info",
                label: intl.get("Responsible").d("Responsible")
            };
        default:
            return {
                icon: <PersonIcon fontSize="small" />,
                color: "default",
                label: intl.get("User").d("User")
            };
    }
};

/**
 * Get initials from name
 */
const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * UsersTab - Displays users linked to the institution
 */
export const UsersTab = ({ users = [],
  onRefresh = null}) => {
    return (
        <Box>
            {/* Header with refresh */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get("LinkedUsers").d("Linked Users")}
                </Typography>
                {onRefresh && (
                    <Tooltip title={intl.get("Refresh").d("Refresh")}>
                        <IconButton size="small" onClick={onRefresh}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Users List */}
            {users.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                        {intl.get("NoUsersLinked").d("No users linked to this institution")}
                    </Typography>
                </Box>
            ) : (
                <List disablePadding>
                    {users.map((user, index) => {
                        const userRoles = user.roles || [user.role];
                        const primaryRole = userRoles[0];
                        const primaryRoleInfo = getRoleInfo(primaryRole);
                        return (
                            <ListItem
                                key={user.pk || index}
                                divider={index < users.length - 1}
                                sx={{ px: 0 }}
                            >
                                <ListItemAvatar>
                                    <Avatar
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            fontSize: "0.875rem",
                                            bgcolor: `${primaryRoleInfo.color}.light`,
                                            color: `${primaryRoleInfo.color}.main`
                                        }}
                                    >
                                        {getInitials(user.name || user.user_name)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={user.name || user.user_name}
                                    secondary={user.email}
                                    primaryTypographyProps={{ variant: "body2" }}
                                    secondaryTypographyProps={{ variant: "caption" }}
                                />
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end" }}>
                                    {userRoles.filter(Boolean).map((role, idx) => {
                                        const roleInfo = getRoleInfo(role);
                                        return (
                                            <Chip
                                                key={idx}
                                                icon={roleInfo.icon}
                                                label={roleInfo.label}
                                                color={roleInfo.color}
                                                size="small"
                                                variant="outlined"
                                            />
                                        );
                                    })}
                                </Box>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

UsersTab.propTypes = {
    users: PropTypes.array,
    onRefresh: PropTypes.func
};

